import type { StripeAppearance, StripePaymentElementOptions } from '@/shared/stripe/stripeTypes';

/**
 * Appearance API values that make the Stripe iframe fields read as Mantine `size="sm"`
 * inputs on this dashboard (theme.ts: Inter stack, brand blue #0070f3, radius sm = 4px;
 * Mantine input: gray-4 border, 36px tall, 14px text). Inter is a system fallback
 * stack here (no webfont is served), so no `fonts` entry is passed.
 */
export const STRIPE_CARD_APPEARANCE: StripeAppearance = {
  theme: 'stripe',
  variables: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSizeBase: '14px',
    fontWeightNormal: '400',
    colorPrimary: '#0070f3',
    colorBackground: '#ffffff',
    colorText: '#212529',
    colorTextSecondary: '#868e96',
    colorTextPlaceholder: '#adb5bd',
    colorDanger: '#fa5252',
    borderRadius: '4px',
    spacingUnit: '4px',
    gridRowSpacing: '12px',
    gridColumnSpacing: '12px',
  },
  rules: {
    '.Input': {
      borderColor: '#ced4da',
      boxShadow: 'none',
      padding: '7px 12px',
      fontSize: '14px',
      lineHeight: '20px',
    },
    '.Input:focus': {
      borderColor: '#0070f3',
      boxShadow: 'none',
      outline: 'none',
    },
    '.Input--invalid': {
      borderColor: '#fa5252',
      boxShadow: 'none',
    },
    '.Label': {
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '4px',
    },
    '.Error': {
      fontSize: '12px',
    },
  },
};

/**
 * Card-only Payment Element (owner ruling: no wallets, no Link, no Stripe branding
 * beyond the secure fields). The SetupIntent is card-only server-side; these options
 * keep the client from adding wallets on capable devices. Name and address are
 * collected by the dashboard form and passed on confirm, so the Element shows only
 * card number, expiry and CVC. Only those two fields are opted out: Stripe.js THROWS
 * at confirmSetup for every field set to 'never' that is not passed back, and
 * `billingDetails: 'never'` would demand email and phone too (cards never collect
 * them under 'auto', so nothing is shown and nothing is owed). `terms.card: 'auto'`
 * keeps the one-line off-session mandate the card networks require for saved cards
 * (notes/billing-card-entry.md D3/D4).
 */
export const STRIPE_CARD_ELEMENT_OPTIONS: StripePaymentElementOptions = {
  layout: 'tabs',
  fields: { billingDetails: { name: 'never', address: 'never' } },
  wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
  terms: { card: 'auto' },
};
